// https://www.jsonrpc.org/specification

const endpoint = '/api';
const HEADERS = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
};

/**
 * Represents an error from the rpc enpdoint.
 * Negative number codes are internal errrors.
 */
export interface RPCErrorDetails {
    message: string;
    data?: string;
}

const RPCErrorDetails = {
    guard: (error: unknown): error is RPCErrorDetails =>
        typeof error === 'object' &&
        error !== null &&
        error.hasOwnProperty('message') &&
        // Shouldn't there be a way to do this without lint exception??
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        typeof (error as any).message === 'string',
};

export class RPCError extends Error {
    constructor(public rpcError: RPCErrorDetails) {
        super('JSONRPCError');
    }
    public toString(): string {
        return `${super.toString()}: ${this.rpcError.message}`;
    }
}

/**
 * Call the JSON-RPC endpoint.
 *
 * Either returns a promise to T (the expected result) or throws a
 * JSONRPCError.
 *
 * We only expose the first param for now.
 */
export const rpc = async <T>(method: string, param: object): Promise<T> => {
    const body = JSON.stringify(param);
    const response = await fetch(`${endpoint}/${method}`, {
        method: 'POST',
        mode: 'no-cors',
        headers: HEADERS,
        body,
    });
    const text = await response.text();
    let json: unknown;
    try {
        json = JSON.parse(text);
    } catch (e) {
        throw new RPCError({
            message: 'Response was not JSON-formatted',
            data: text,
        });
    }

    if (response.status !== 200) {
        if (RPCErrorDetails.guard(json)) {
            throw new RPCError(json);
        }
        throw new RPCError({
            message: `Unexpected response code returned: ${response.status}`,
            data: text,
        });
    }

    return json as T;
};
